<template>
  <div class="general-tab-container">
    <div class="general-tab-container__btn-wrapper no-internal-print">
      <div class="general-tab-container__btn-group">
        <b-button
          class="general-tab-container__generic-button"
          variant="primary"
          :disabled="!hasPendingSamples"
          @click="openReprocessPendingSamples"
        >
          {{ $t('engineering.reprocess-pending-samples') }}
        </b-button>
      </div>
      <div>
        <list-aggregator
          modal-item-id-key="id"
          :modal-table-header="aggregatorHeaders"
          :modal-table-items="selectedSamples"
          :modal-title="assetModule.name"
          :modal-item-name-key="['number', 'lot']"
          :configuration-mode="true"
          @update-selection="updateSelection"
        />
      </div>
      <div class="general-tab-container__btn-group">
        <b-button
          class="general-tab-container__generic-button"
          variant="outline-danger"
          :class="{ 'mr-2': !isFontSizeBig }"
          @click="deleteSample"
          :disabled="!selectedSamples.length"
        >
          <s-icon
            name="delete"
            class="mr-2"
          />
          {{ $t('actions.delete') }}
        </b-button>
        <b-button
          class="general-tab-container__generic-button"
          variant="outline-secondary"
          :class="{ 'mr-2': !isFontSizeBig }"
          :disabled="singleItemSelected"
          @click="editSampleRedirect"
        >
          <s-icon name="edit-paper" class="mr-2" />
          {{ $t('actions.edit') }}
        </b-button>
        <b-button
          class="general-tab-container__generic-button"
          variant="outline-success"
          :disabled="!selectedSamples.length"
          @click="redirectToLotsView"
        >
          <s-icon name="add" class="mr-2" />
          {{ $t('engineering.reports.label') }}
        </b-button>
      </div>
    </div>
    <b-row v-if="!items.length">
      <b-col cols="12">
        <s-empty-list :loading="loadingList" />
      </b-col>
    </b-row>
    <template v-else>
      <b-row class="no-internal-print">
        <b-col
          cols="12"
          class="mt-3 general-tab-container__grid"
        >
          <s-table
            bordered
            class="no-align"
            :items="items"
            :fields="fields"
          >
            <template
              v-for="field in fields"
              #[`cell(${field.key})`]="{ item }"
            >
              <span
                :key="field.key"
                :class="labelClassHandler(item, field)"
                class="text-nowrap general-table__field"
              >
                {{ getFormattedValue(item, field) }}
                <div class="ml-2 general-table__alert" v-if="showAlertIcon(item, field)">
                  <i
                    aria-hidden="true"
                    class="fas fa-exclamation-triangle"
                  />
                </div>
              </span>
            </template>
            <template #head(check)>
              <div class="paged-grid__check-wrapper">
                <b-form-checkbox
                  @change="selectAll"
                  :checked="selectAllHandler"
                />
              </div>
            </template>
            <template #cell(check)="data">
              <b-form-checkbox
                :checked="checkItemSelected(data.item)"
                @change="toggleItemSelection(data.item, $event)"
              />
            </template>
          </s-table>
        </b-col>
      </b-row>
      <b-col class="no-internal-print">
        <grid-paginator
          @input="getTableItems"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total-rows="pagination.totalRows"
          class="alarms-classification__paginator pt-3"
          size="normal-side-menu"
        />
      </b-col>
    </template>
    <b-row class="mt-5">
      <b-col cols="12">
        <label class="text-success">
          {{ $t('engineering.physical-chemical.general-tab.evolution') }}
        </label>
      </b-col>
    </b-row>
    <b-row class="px-3">
      <s-loader
        v-if="loading"
        class="w-100 p-5"
        size="lg"
      />
      <default-chart
        v-else
        x-data-manager="dateTime"
        chart-id="physical-chemical"
        :chart-data="chartData"
        :loading="loadingChart"
        :remove-outlier.sync="removeChartOutlier"
        :enable-multi-axis.sync="enableChartMultiAxis"
        :time-zone="timeZoneUTC"
        @toggle-outlier="getEvolutionChart(true)"
        @toggle-multi-axis="getEvolutionChart(true)"
      />
    </b-row>
    <filter-modal
      :show-modal="showModal"
      v-model="showModal"
    />
    <process-handler-modal
      v-model="showReprocessModal"
      @submit-process-request="submitProcessRequest"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DefaultChart from '@/components/charts/DefaultChart';
import ProcessHandlerModal from '@/domain/components/installation/engineering/ProcessHandlerModal';
import eventBus from '@/services/globalEventBus';
import engineeringEvents from '@/domain/events/engineering';
import GeneralTabTableFields
  from '@/domain/data/installation/engineering/physicalChemical/general-tab-table-fields.json';
import GridPaginator from '@/components/lists/Paginator';
import Service from '@/domain/services/installation/engineering/physicalChemical/generalTab';
import tabNames from '@/domain/enums/installation/engineering/physicalChemical/physicalChemicalTabs';
import loadingMixin, { build as loadingMixinBuild } from '@/services/loadingMixin';
import defaultChartMixin from '@/domain/mixins/defaultChartMixin';
import {
  formatter as dateTimeFormatter,
  setToStartOfDay,
} from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import ListAggregator from '@/components/listAggregator/index';
import FilterModal from './FilterModal';

const EMPTY_CELL_VALUE = '-';

const SAMPLE_NUMBER_FIELD_NAME = 'number';

export default {
  name: 'GeneralTable',
  props: {
    assetModule: Object,
  },
  components: {
    FilterModal,
    GridPaginator,
    ProcessHandlerModal,
    DefaultChart,
    ListAggregator,
  },
  mixins: [
    loadingMixinBuild('list'),
    loadingMixin,
    defaultChartMixin,
  ],
  data: () => ({
    selectedSamples: [],
    chartData: [],
    showModal: false,
    showReprocessModal: false,
    hasPendingSamples: false,
    amountOfPendingLots: 0,
    pagination: {
      perPage: 10,
      totalRows: null,
      currentPage: 1,
    },
    items: [],
    timeZoneUTC: timezones.UTC,
  }),
  computed: {
    ...mapGetters([
      'currentFontSize',
    ]),
    selectAllHandler() {
      return this.items.every(tableItem => this.selectedSamples.some(
        selectedSample => tableItem.id === selectedSample.id,
      ));
    },
    selectedSamplesIds() {
      return this.selectedSamples.map(selectedSample => selectedSample.id);
    },
    aggregatorHeaders() {
      return [
        this.$t('fields.engineering.chromatography.analysis.sample-number'),
        this.$t('fields.engineering.chromatography.analysis.lot-number'),
      ];
    },
    isFontSizeBig() {
      return this.currentFontSize.includes('18');
    },
    showEmptyChart() {
      return !this.chartData || !this.chartData.length;
    },
    singleItemSelected() {
      return this.selectedSamples.length !== 1;
    },
    fields() {
      return GeneralTabTableFields.map(item => ({
        ...item,
        label: this.$t(item.label, item),
      }));
    },
    lastPage() {
      return Math.ceil(this.pagination.totalRows / this.pagination.perPage);
    },
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    this.deinit();
  },
  methods: {
    showMA045Message(error) {
      const samples = error.response.data.parametros.amostras;
      this.$pushAlert({
        type: 'warning',
        title: this.$t('messages.MA045.title'),
        message: this.$t('messages.MA045.message'),
        messageList: samples.map(sample => ({
          message: this.$t('messages.MA045.iterator', {
            Lote: sample.lote,
            DataHoraAmostra: this.formatDateTime(sample.dataHoraAmostragem),
          }),
        })),
      });
    },
    formatDateTime(date) {
      return dateTimeFormatter(date);
    },
    deleteSample() {
      this.$confirmMessage({ code: 'MC034' }).accepted(() => {
        const { selectedSamplesIds } = this;
        Service.deleteSample(this.assetModule.id, selectedSamplesIds)
          .then(() => {
            this.$pushAlert({ code: 'MS023' });
            this.$set(this.pagination, 'currentPage', this.getPageNumberOnDelete(selectedSamplesIds));
          })
          .catch((error) => {
            const messageCode = error.response.data && error.response.data.codigoMensagem;
            if (messageCode === 'MA045') {
              this.showMA045Message(error);
            }
          })
          .finally(() => {
            this.getTableItems();
            this.getEvolutionChart();
            this.selectedSamples = [];
          });
      });
    },
    getPageNumberOnDelete(selectedSamplesIds) {
      const { currentPage } = this.pagination;
      const currentPageIfLastSample = (currentPage - 1) || 1;

      const allSamplesFromPageDeleted = this.items
        && (this.items.length - selectedSamplesIds.length === 0);

      const isCurrentPageLast = currentPage === this.lastPage;

      return isCurrentPageLast && allSamplesFromPageDeleted ? currentPageIfLastSample : currentPage;
    },
    editSampleRedirect() {
      this.$confirmMessage({ code: 'MC036' }).accepted(() => {
        const { id } = this.selectedSamples[0];
        this.$router.push({
          name: 'monitoring.engineering.module',
          params: {
            sampleListPage: this.pagination.currentPage,
          },
          query: {
            tab: tabNames.SAMPLE,
            sampleId: id,
          },
        });
      });
    },
    selectAll(selectAllValue) {
      if (selectAllValue) {
        this.items.forEach(item => this.addSelectedItem(item));
        return;
      }
      this.items.forEach(item => this.removeSelectedItem(item));
    },
    checkItemSelected(item) {
      return !!this.selectedSamples.find(selectedSample => selectedSample.id === item.id);
    },
    toggleItemSelection(selection, $event) {
      if ($event) {
        this.addSelectedItem(selection);
        return;
      }
      this.removeSelectedItem(selection);
    },
    addSelectedItem(selectedItem) {
      const itemExists = this.selectedSamples.some(
        selectedSample => selectedSample.id === selectedItem.id,
      );
      if (itemExists) return;
      this.selectedSamples.push(selectedItem);
    },
    removeSelectedItem(selectedItem) {
      const itemIndex = this.selectedSamples.findIndex(
        (selectedSample => selectedSample.id === selectedItem.id),
      );
      this.selectedSamples.splice(itemIndex, 1);
    },
    updateSelection(updatedSelectedSamples) {
      this.selectedSamples = updatedSelectedSamples;
    },
    getFormattedValue(data, { key }) {
      const value = data[key];
      let formattedValue = value;

      const dataFormatter = Service.sampleListRenderingHelpers.dataFormatters[key];

      if (dataFormatter) {
        formattedValue = dataFormatter(value, data, key);
      }

      if (formattedValue == null) {
        return EMPTY_CELL_VALUE;
      }

      if (typeof formattedValue === 'number') {
        formattedValue = this.$n(formattedValue);
      }

      const translationResolver = Service.sampleListRenderingHelpers
        .valueTranslationPathResolvers[key];

      if (translationResolver) {
        formattedValue = this.$t(translationResolver(value));
      }

      return formattedValue;
    },
    showAlertIcon({ alarmedFields }, { key }) {
      if (!alarmedFields || !alarmedFields.length) return '';
      return key === SAMPLE_NUMBER_FIELD_NAME && alarmedFields.length;
    },
    labelClassHandler({ alarmedFields }, { key }) {
      if (!alarmedFields || !alarmedFields.length) return '';
      if (key === SAMPLE_NUMBER_FIELD_NAME && alarmedFields.length) {
        return ['text-red', 'font-weight-bold'];
      }
      return { 'text-red': alarmedFields.includes(key) };
    },
    async getEvolutionChart(loadingChart) {
      if (!loadingChart) {
        this.startLoading();
      }
      this.loadingChart = loadingChart;
      this.chartData = [];
      Service.getChartData(this.assetModule.id, this.chartFilters)
        .then((response) => {
          this.chartData = response;
        })
        .finally(() => {
          this.endLoading();
          this.loadingChart = false;
        });
    },
    openReprocessPendingSamples() {
      this.showReprocessModal = true;
    },
    submitProcessRequest(userName) {
      const messageCodeDefault = 'MS022';
      const messageCodeWithoutPer017 = 'MS040';
      let messageCode = messageCodeDefault;
      if (!this.$hasUserPermission('PER017')) {
        messageCode = messageCodeWithoutPer017;
      }
      Service.reprocessPendingSamples(this.assetModule.id, { userName })
        .then(() => {
          this.$pushAlert({ code: messageCode });
          this.showReprocessModal = false;
          this.hasPendingSamples = false;
        });
    },
    openFiltersModal() {
      this.showModal = true;
    },
    closeFiltersModal() {
      this.showModal = false;
    },
    redirectToLotsView() {
      const {
        installationId,
        assetId,
        assetModuleId,
      } = this.$route.params;
      const routeData = this.$router.resolve({
        name: 'clean-view.physical-chemical-lots-and-parameters',
        params: {
          installationId,
          assetId,
          assetModuleId,
        },
        query: {
          sampleIds: this.selectedSamplesIds,
        },
      });
      window.open(routeData.href, '_blank');
    },
    refreshPendingLots() {
      this.$emit('change-pending-lots', this.amountOfPendingLots);
    },
    async getTableItems() {
      this.startLoadingList();
      const { perPage, currentPage } = this.pagination;
      const payload = {
        page: currentPage,
        pageSize: perPage,
      };
      try {
        this.items = [];
        await Service.getSampleList(this.assetModule.id, payload)
          .then(({ summary, total, data }) => {
            this.items = this.fixDataValuesInUTC(data);
            this.$set(this.pagination, 'totalRows', total);
            this.$emit('change-pagination', currentPage);
            this.hasPendingSamples = summary.hasPendingSamples;
            this.amountOfPendingLots = summary.amountOfPendingLots;
            this.refreshPendingLots();
          });
      } finally {
        this.endLoadingList();
      }
    },
    fixDataValuesInUTC(data) {
      const values = [];
      const opts = { timeZone: this.timeZoneUTC };
      data.forEach((item) => {
        values.push({
          ...item,
          date: setToStartOfDay(item.sampleDateTime, opts),
          time: dateTimeFormatter(item.sampleDateTime, formats.ISO_TIME, this.timeZoneUTC),
          analyzeDate: setToStartOfDay(item.analysisDateTime, opts),
          analyzeTime: dateTimeFormatter(item.analysisDateTime, formats.ISO_TIME, this.timeZoneUTC),
          });
      });
      return values;
    },
    init() {
      this.refreshData();
      eventBus.on(engineeringEvents.refreshData, this.refreshData, this);
    },
    deinit() {
      eventBus.off(engineeringEvents.refreshData, this.refreshData, this);
    },
    getPageNumber() {
      return (this.$route.params || {}).sampleListPage || 1;
    },
    refreshData() {
      this.$set(this.pagination, 'currentPage', this.getPageNumber());
      this.getTableItems();
      this.getEvolutionChart();
    },
  },
};
</script>
<style lang="sass" scoped>
  @import "src/styles/defaults/mobile-first"

  .btn
    min-width: 0 !important
  .general-table
    &__field
      display: flex
      justify-content: center
      align-items: center
    &__alert
      display: flex
      align-items: center
      justify-content: center
  .general-tab-container
    &__btn-wrapper
      display: flex
      flex-wrap: wrap
      flex-direction: column
      justify-content: space-between
      margin-top: 8px
      +media-min-md
        flex-direction: row
    &__btn-group
      display: inline-flex
    ::v-deep &__generic-button > .btn,
    &__generic-button
      min-height: unset !important
      min-width: unset !important
    &__grid
      width: 400px
      overflow-x: auto
    ::v-deep &__table-head
      text-align: center
      min-width: 75px
      white-space: nowrap
    ::v-deep
      .table-bordered th, .table-bordered td
        border: 2px solid var(--color-background-light) !important
    +media-min-sm()
    +media-min-md()
    +media-min-lg()
  ::v-deep
    .echarts
      textarea
        max-height: 98% !important
      div:last-child
        div
          margin-right: 0 !important
</style>
